"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { AlertTriangle, CheckCircle2, Clock, Swords } from "lucide-react";
import { toast } from "sonner";

import {
  cancelCaptainResult,
  submitCaptainResult,
  type ApiMatchPendingResult,
  type CaptainResultResponse,
} from "@/entities/bracket/api/client";
import { extractApiErrorMessage } from "@/shared/api/error-handler";
import { Button } from "@/shared/ui/buttons/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/ui/cards/card";
import { Alert, AlertDescription } from "@/shared/ui/feedback/alert";
import { cn } from "@/shared/lib/utils";

/**
 * KAN-167: двустороннее подтверждение результата матча капитанами.
 *
 * Капитан одной команды заявляет результат → он «ожидает подтверждения». Капитан второй
 * команды подтверждает (тот же результат → матч закрывается) или заявляет иной (авто-диспут
 * организатору). Организатор по-прежнему может проставить результат сам. Показывается в
 * режиме продвижения «Ручное управление» (см. match-panels.captainResult).
 */
interface CaptainResultCardProps {
  matchId: string;
  bestOf?: number;
  teamA: { id: string; name: string };
  teamB: { id: string; name: string };
  /** Сторона капитана-смотрящего (teamA.id или teamB.id). */
  myTeamId: string;
  pendingResult?: ApiMatchPendingResult | null;
  /** Перечитать матч после изменения. */
  onDone?: () => void;
}

export function CaptainResultCard({
  matchId,
  bestOf = 1,
  teamA,
  teamB,
  myTeamId,
  pendingResult,
  onDone,
}: CaptainResultCardProps) {
  const tUi = useTranslations("Ui");
  const tToast = useTranslations("Toasts");
  const winsNeeded = Math.floor((bestOf > 0 ? bestOf : 1) / 2) + 1;
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [winnerId, setWinnerId] = useState<string>(myTeamId);
  const [loserGames, setLoserGames] = useState(0);

  const nameOf = (id?: string | null) =>
    id === teamA.id ? teamA.name : id === teamB.id ? teamB.name : tUi("litCaptainResultCard.komanda");

  const hasPending = Boolean(pendingResult?.byUserId);
  const mine = hasPending && pendingResult?.byTeamId === myTeamId;

  const applyOutcome = (r: CaptainResultResponse) => {
    switch (r.status) {
      case "Pending":
        toast.success(tToast("captainResultCard.rezultatZayavlenZhdemPodtverzhdeniya"));
        break;
      case "Finalized":
        toast.success(tToast("captainResultCard.rezultatPodtverzhdenObeimiKomandami"));
        break;
      case "Disputed":
        toast.warning(tToast("captainResultCard.rezultatyRazoshlisOtkrytDisput"));
        break;
      default:
        toast.warning(tToast("captainResultCard.rezultatyRazoshlisObratitesK"));
    }
    setShowForm(false);
    onDone?.();
  };

  const doSubmit = async (wId: string, t1: number, t2: number) => {
    if (busy) return;
    setBusy(true);
    try {
      applyOutcome(await submitCaptainResult(matchId, { winnerId: wId, team1Score: t1, team2Score: t2 }));
    } catch (e) {
      toast.error(extractApiErrorMessage(e, tUi("litCaptainResultCard.neUdalosOtpravitRezultat")));
    } finally {
      setBusy(false);
    }
  };

  const submitForm = () => {
    const t1 = winnerId === teamA.id ? winsNeeded : loserGames;
    const t2 = winnerId === teamB.id ? winsNeeded : loserGames;
    void doSubmit(winnerId, t1, t2);
  };

  const confirmPending = () => {
    if (!pendingResult?.winnerId) return;
    void doSubmit(
      pendingResult.winnerId,
      pendingResult.team1Score ?? 0,
      pendingResult.team2Score ?? 0,
    );
  };

  const cancelPending = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await cancelCaptainResult(matchId);
      toast.success(tToast("captainResultCard.zayavkaOtozvana"));
      onDone?.();
    } catch (e) {
      toast.error(extractApiErrorMessage(e, tUi("litCaptainResultCard.neUdalosOtozvatZayavku")));
    } finally {
      setBusy(false);
    }
  };

  // ── Своя заявка ждёт соперника ──
  const pendingMineView = pendingResult && (
    <Alert className="border-primary/30 bg-primary/5">
      <Clock className="h-4 w-4" />
      <AlertDescription className="space-y-2">
        <div>
          {tUi("captainResultCard.vyZayavili")} <b>{nameOf(pendingResult.winnerId)}</b> побеждает{" "}
          {pendingResult.team1Score}:{pendingResult.team2Score}. Ждём подтверждения соперника.
        </div>
        <Button variant="outline" size="sm" onClick={cancelPending} disabled={busy}>
          {tUi("captainResultCard.otozvatZayavku")}
        </Button>
      </AlertDescription>
    </Alert>
  );

  // ── Соперник заявил — подтвердить или оспорить ──
  const pendingOpponentView = pendingResult && (
    <div className="space-y-3">
      <Alert>
        <CheckCircle2 className="h-4 w-4" />
        <AlertDescription>
          {tUi("captainResultCard.sopernikZayavil")} <b>{nameOf(pendingResult.winnerId)}</b> побеждает{" "}
          {pendingResult.team1Score}:{pendingResult.team2Score}. Подтвердите, если согласны.
        </AlertDescription>
      </Alert>
      <div className="flex flex-wrap gap-2">
        <Button onClick={confirmPending} disabled={busy}>
          <CheckCircle2 className="mr-2 h-4 w-4" />
          {tUi("captainResultCard.podtverdit")}
        </Button>
        <Button variant="outline" onClick={() => setShowForm(true)} disabled={busy}>
          <AlertTriangle className="mr-2 h-4 w-4" />
          {tUi("captainResultCard.osporit")}
        </Button>
      </div>
    </div>
  );

  // ── Форма заявки результата ──
  const winnerButton = (team: { id: string; name: string }) => (
    <Button
      key={team.id}
      type="button"
      variant={winnerId === team.id ? "default" : "outline"}
      size="sm"
      onClick={() => setWinnerId(team.id)}
      disabled={busy}
    >
      {team.name}
    </Button>
  );

  const formView = (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <div className="text-xs font-medium text-muted-foreground">{tUi("captainResultCard.pobeditel")}</div>
        <div className="flex flex-wrap gap-2">
          {winnerButton(teamA)}
          {winnerButton(teamB)}
        </div>
      </div>

      {winsNeeded > 1 && (
        <div className="space-y-1.5">
          <div className="text-xs font-medium text-muted-foreground">
            Счёт проигравшего (побеждает {winsNeeded})
          </div>
          <div className="flex flex-wrap gap-2">
            {Array.from({ length: winsNeeded }).map((_, g) => (
              <Button
                key={g}
                type="button"
                variant={loserGames === g ? "default" : "outline"}
                size="sm"
                className={cn("w-10", loserGames === g && "pointer-events-none")}
                onClick={() => setLoserGames(g)}
                disabled={busy}
              >
                {g}
              </Button>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button onClick={submitForm} disabled={busy}>
          <Swords className="mr-2 h-4 w-4" />
          {tUi("captainResultCard.zayavitRezultat")}
        </Button>
        {hasPending && (
          <Button variant="ghost" onClick={() => setShowForm(false)} disabled={busy}>
            {tUi("captainResultCard.otmena")}
          </Button>
        )}
      </div>
    </div>
  );

  const body =
    hasPending && !showForm ? (mine ? pendingMineView : pendingOpponentView) : formView;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{tUi("captainResultCard.podtverzhdenieRezultata")}</CardTitle>
        <CardDescription>
          {tUi("captainResultCard.kapitanyKomandZayavlyayutRezultat")}
        </CardDescription>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
}
